import { useCallback } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { getIdToken } from "../../auth/auth";
import { axiosInstance } from "../../lib/axios";
import { getActiveWorkspaceId } from "../../workspace";
import { INTEGRATION_SCOPES, isNonOAuthIntegration } from "../constants";

import { useDelinkIntegration } from "./useDelinkIntegration";
import { INTEGRATIONS_QUERY_KEY } from "./useFetchAllIntegrations";

import type { IntegrationScope } from "../constants";
import type { DelinkIntegrationRequest } from "../types/integrations";

/**
 * Comprehensive hook for integration actions (connect, delink, etc.)
 * Provides unified interface for all integration operations
 *
 * A page operates at a single tier, so the scope is fixed for the hook rather than
 * passed per call.
 */
export const useIntegrationActions = (
  integrationScope: IntegrationScope = INTEGRATION_SCOPES.USER,
) => {
  const queryClient = useQueryClient();

  // Get delink mutation
  const { mutate: delinkMutation, isPending: isDelinkPending } =
    useDelinkIntegration();

  // Connect integration mutation for non-OAuth integrations
  const connectMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      return await axiosInstance.post(`/connect/${sourceId}`);
    },
    onSuccess: () => {
      // Invalidate integrations list to refresh connection status
      queryClient.invalidateQueries({
        queryKey: INTEGRATIONS_QUERY_KEY(integrationScope),
      });
    },
  });

  // OAuth handler function
  const handleOAuthConnect = useCallback(
    async (source: string) => {
      const jwt = await getIdToken();

      if (!jwt) {
        throw new Error("Failed to retrieve JWT");
      }

      const baseUrl = import.meta.env.DEV
        ? `${window.location.origin}/api`
        : import.meta.env.VITE_API_URL;

      const oauthUrl = new URL(`${baseUrl}/oauth/${source}`);
      oauthUrl.searchParams.append("state", jwt);
      oauthUrl.searchParams.append("return_url", window.location.href);
      oauthUrl.searchParams.append("integration_scope", integrationScope);
      const workspaceId = getActiveWorkspaceId();
      if (workspaceId) {
        oauthUrl.searchParams.append("workspace_id", workspaceId);
      }

      console.log(`Redirecting to OAuth URL: ${oauthUrl.toString()}`);
      window.open(oauthUrl.toString(), "_self");
    },
    [integrationScope],
  );

  // Connect integration function that handles both OAuth and non-OAuth integrations
  const connectIntegration = useCallback(
    (sourceId: string) => {
      if (isNonOAuthIntegration(sourceId)) {
        // Use existing connectMutation for non-OAuth integrations
        connectMutation.mutate(sourceId);
      } else {
        // Use OAuth handler for other integrations
        handleOAuthConnect(sourceId);
      }
    },
    [connectMutation, handleOAuthConnect],
  );

  // Unified delink function that fetches connected details and then delinks
  const delinkIntegration = useCallback(
    async (sourceId: string) => {
      try {
        // First, get the connected integration details
        const response = await axiosInstance.get(`/connect/${sourceId}`, {
          params: { integration_scope: integrationScope },
        });
        const connectedIntegration = response.data.results?.[0];

        if (!connectedIntegration) {
          throw new Error("No connected integration found");
        }

        // Create delink request
        const delinkRequest: DelinkIntegrationRequest = {
          verifiableId: connectedIntegration.verifiable_id,
          source: connectedIntegration.source,
          integrationScope,
        };

        // Execute delink mutation
        return new Promise<void>((resolve, reject) => {
          delinkMutation(delinkRequest, {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          });
        });
      } catch (error) {
        console.error("Failed to delink integration:", error);
        throw error;
      }
    },
    [delinkMutation, integrationScope],
  );

  return {
    // Actions
    connectIntegration,
    delinkIntegration,

    // Loading states
    isConnecting: connectMutation.isPending,
    isDelinking: isDelinkPending,
    isLoading: connectMutation.isPending || isDelinkPending,

    // Errors
    connectError: connectMutation.error,
    delinkError: null, // Delink errors are handled by useDelinkIntegration
  };
};
